import React, { useEffect } from 'react';
import { Box, CardContent, Grid, Typography, Chip, Button } from '@mui/material';
import { 
  proposals,
  useCases
} from './data/mockData.ts';
import { motion } from 'framer-motion';
import FloatingNotifications from './components/FloatingNotifications.tsx';
import NavigationPathways from './components/NavigationPathways.tsx';
import SurrogateCard from './components/SurrogateCard.tsx';
import ProblemCategoryCard from './components/ProblemCategoryCard.tsx';
import UseCaseLinks from './components/UseCaseLinks.tsx';
import ExecutiveBlocks from './components/ExecutiveBlocks.tsx';
import BackgroundAnimation from './components/BackgroundAnimation.tsx';
import { useLandingStore } from './store/landingStore.ts';
import BuilderContainer from '../../components/BuilderContainer.tsx';
import NotificationCube from './components/NotificationCube.tsx';
import GradientText from '../../components/common/GradientText.tsx';
import SubmitProposalButton from '../../components/common/SubmitProposalButton.tsx';
import HolographicContainer from '../../components/common/HolographicContainer.tsx';
import ProposalCard from '../../components/common/ProposalCard.tsx';
import ProblemSourcing from './components/ProblemSourcing.tsx';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import SurrogateAvatar from '../../components/common/SurrogateAvatar.tsx';
import { Surrogate } from './types';

const problemCategories = [
  {
    id: '1',
    title: 'AI & Automation',
    description: 'Problems related to implementing AI solutions and automating business processes.',
    problemCount: 32,
    icon: 'ai' as const,
  },
  {
    id: '2',
    title: 'Security & Compliance',
    description: 'Problems addressing security vulnerabilities, data protection, and regulatory compliance.',
    problemCount: 24,
    icon: 'security' as const,
  },
  {
    id: '3',
    title: 'Business Planning',
    description: 'Strategic planning, market analysis, and business development challenges.',
    problemCount: 18,
    icon: 'business' as const,
  },
];

// Create a styled wrapper for the BuilderContainer with the background image
const BuilderSectionWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  marginBottom: theme.spacing(8),
  overflow: 'hidden',
  borderRadius: theme.spacing(2),
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    //backgroundImage: 'url("/images/The_Eterna_Cognitive_Nexus.png")', // removed 
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    opacity: 0.7,
    filter: 'blur(1px)', // Reduced blur to show more detail
    transform: 'scale(1.05)', // Slightly scale up to avoid blur edges
    zIndex: 0,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(to bottom, rgba(26, 26, 46, 0.4), rgba(26, 26, 46, 0.2))', // Lighter overlay
    zIndex: 0,
  }
}));

// Define marketplace surrogates (different from the Surrogate type in types.ts)
const marketplaceSurrogates = [
  {
    id: '1',
    name: 'Alpha',
    role: 'Assistant',
    image: 'Alpha',
    specialty: 'Natural Language Processing',
    status: 'active',
    rating: 4.8,
    completedTasks: 1234
  },
  {
    id: '2',
    name: 'Beta',
    role: 'Researcher',
    image: 'Beta',
    specialty: 'Data Analysis',
    status: 'active',
    rating: 4.6,
    completedTasks: 987
  },
  {
    id: '3',
    name: 'Gamma',
    role: 'Developer',
    image: 'Gamma',
    specialty: 'Code Generation',
    status: 'active',
    rating: 4.9,
    completedTasks: 2341
  },
  {
    id: '4',
    name: 'Delta',
    role: 'Strategist',
    image: 'Delta',
    specialty: 'Business Planning',
    status: 'active',
    rating: 4.7,
    completedTasks: 1567
  }
];

// Define surrogates that match the Surrogate type in types.ts for use with SurrogateCard
const typedSurrogates: Surrogate[] = [
  {
    id: '1',
    name: 'Alpha AI',
    type: 'ai',
    coreSkills: ['Natural Language Processing', 'Intent Recognition', 'Sentiment Analysis'],
    automationCapability: 9,
    efficiency: 8
  },
  {
    id: '2',
    name: 'Beta AI',
    type: 'ai',
    coreSkills: ['Data Analysis', 'Pattern Recognition', 'Predictive Modeling'],
    automationCapability: 8,
    efficiency: 7
  },
  {
    id: '3',
    name: 'Gamma AI',
    type: 'ai',
    coreSkills: ['Code Generation', 'Bug Detection', 'Code Optimization'],
    automationCapability: 9,
    efficiency: 9
  }
];

const Landing: React.FC = () => {
  const { notifications, addNotification } = useLandingStore();
  const navigate = useNavigate();

  useEffect(() => {
    // Demo notification
    const demoNotification = {
      title: 'Eterna',
      message: 'The AI Marketplace',
      position: { x: 20, y: 20 },
      type: 'info' as const
    };
    addNotification(demoNotification);
  }, [addNotification]);

  const handleViewProposal = (id: string) => {
    navigate(`/proposal-detail/${id}`);
  };

  const AgentCard = styled(motion(HolographicContainer))(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    '& .MuiCardContent-root': {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
    }
  }));
  
  const StyledChip = styled(Chip)(({ theme }) => ({
    background: 'rgba(138, 43, 226, 0.1)',
    border: '1px solid rgba(138, 43, 226, 0.2)',
    color: '#8a2be2',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'rgba(0, 255, 255, 0.1)',
      border: '1px solid rgba(0, 255, 255, 0.3)',
      color: '#00ffff',
      boxShadow: '0 0 10px rgba(0, 255, 255, 0.3)',
    }
  }));
  
  const ConnectButton = styled(Button)(({ theme }) => ({
    background: 'linear-gradient(45deg, #003366 30%, #8A2BE2 90%)',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'linear-gradient(45deg, #00CC99 30%, #00ffff 90%)',
      boxShadow: '0 0 15px rgba(0, 255, 255, 0.5)',
    }
  }));

  return (
    <Box sx={{ 
      position: 'relative', 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      pt: 0,
      pb: 20,
      overflow: 'hidden'
    }}>
      <BackgroundAnimation />
      {/* Executive Blocks Section - Now at top */}
      <Box sx={{ 
        position: 'fixed',
        top: 0,
        left: 150,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        pt: 2,
        pb: 2,
        zIndex: 100,
        background: 'rgba(26, 26, 46, 0.6)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(138, 43, 226, 0.2)',
        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
      }}>
        <ExecutiveBlocks surrogates={typedSurrogates} />
      </Box>

      {/* Main Content - Adjusted top padding to account for ExecutiveBlocks */}
      <main className="container mx-auto px-4" style={{ paddingTop: '220px' }}>
        <section className="mb-16">
        </section>

        {/* Problem Sourcing Section */}
        <section className="mb-16">
          <ProblemSourcing />
        </section>

        {/* Proposals Section */}
        <section className="mb-16">
          <Box sx={{ 
            width: '100%', 
            maxWidth: '1200px',
            mb: 8,
            position: 'relative',
            px: 2,
            mx: 'auto'
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 4
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GradientText variant="h4" gradient="primary">
                  Active Proposals
                </GradientText>
              </Box>
              <SubmitProposalButton />
            </Box>
            
            <Grid container spacing={3}>
              {proposals.slice(0, 3).map((proposal) => (
                <Grid item xs={12} md={4} key={proposal.id}>
                  <ProposalCard 
                    proposal={proposal} 
                    onViewDetails={handleViewProposal} 
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </section>

        {/* Surrogate Avatar Section */}
        <section className="mb-16">
          <Box sx={{ 
            width: '100%', 
            maxWidth: '1200px',
            mb: 8,
            position: 'relative',
            px: 2,
            mx: 'auto'
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 4
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GradientText variant="h4" gradient="primary">
                  Active Surrogates
                </GradientText>
              </Box>
              <GradientText variant='h6' gradient="primary" underline="always">
                  View All
                </GradientText>
              </Box>
            <Grid container spacing={4}>
              {marketplaceSurrogates.map((surrogate, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={surrogate.id}>
                  <AgentCard
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <SurrogateAvatar
                          name={surrogate.name}
                          role={surrogate.role}
                          image={surrogate.image}
                          status={surrogate.status as 'active' | 'idle' | 'busy'}
                          size="medium"
                          showName={true}
                        />
                      </Box>

                      <StyledChip label={surrogate.specialty} size="small" sx={{ mb: 2 }} />

                      <Box sx={{ mb: 2, flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ 
                          color: 'rgba(255, 255, 255, 0.6)',
                          transition: 'all 0.3s ease',
                          '&:hover': { color: 'rgba(255, 255, 255, 0.9)' }
                        }}>
                          Rating: {surrogate.rating}/5.0
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: 'rgba(255, 255, 255, 0.6)',
                          transition: 'all 0.3s ease',
                          '&:hover': { color: 'rgba(255, 255, 255, 0.9)' }
                        }}>
                          Tasks: {surrogate.completedTasks}
                        </Typography>
                      </Box>

                      <ConnectButton variant="contained" fullWidth>
                        Connect
                      </ConnectButton>
                    </CardContent>
                  </AgentCard>
                </Grid>
              ))}
            </Grid>
          </Box>
        </section>
        
        {/* Builder Section */}
        <section className="mb-16">
          <BuilderSectionWrapper>
            <BuilderContainer />
          </BuilderSectionWrapper>
        </section>

        {/* Surrogate Cards Section */}
        <section className="mb-16">
          <Box sx={{ 
            width: '100%', 
            maxWidth: '1200px',
            mb: 8,
            position: 'relative',
            px: 2,
            mx: 'auto'
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 4
            }}>
              <GradientText variant='h4' gradient="primary">
                AI Featured Surrogates
              </GradientText>
              <GradientText variant='h6' gradient="primary" underline="always">
                View All
              </GradientText>
            </Box>
            <Grid container spacing={4}>
              {typedSurrogates.slice(0, 3).map((surrogate) => (
                <Grid item xs={12} sm={6} md={4} key={surrogate.id}>
                  <SurrogateCard surrogate={surrogate} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </section>

        {/* Problem Categories Section */}
        <section className="mb-16">
          <Box sx={{ 
            width: '100%', 
            maxWidth: '1200px',
            mb: 8,
            position: 'relative',
            px: 2,
            mx: 'auto'
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 4
            }}>
              <GradientText variant='h4' gradient="primary">
                AI Suggested Problems
              </GradientText>
              <GradientText variant='h6' gradient="primary" underline="always">
                View All
              </GradientText>
            </Box>
            <Grid container spacing={4}>
              {problemCategories.map((category, index) => (
                <Grid item xs={12} sm={6} md={4} key={category.id}>
                  <ProblemCategoryCard category={category} index={index} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </section>

        {/* Use Cases Section */}
        <section className="mb-16">
          <Box sx={{ 
            width: '100%', 
            maxWidth: '1200px',
            mb: 8,
            position: 'relative',
            px: 2,
            mx: 'auto'
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 4
            }}>
              <GradientText variant='h4' gradient="primary">
                AI Suggested Use Cases
              </GradientText>
              <GradientText variant='h6' gradient="primary" underline="always">
                View All
              </GradientText>
            </Box>
            <UseCaseLinks useCases={useCases.slice(0, 4)} />
          </Box>
        </section>
      </main>

      {/* Navigation Section - Now at bottom without ExecutiveBlocks */}
      <Box sx={{ 
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-end',
        pb: 4,
        zIndex: 100,
        background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)',
        pt: 8
      }}>
        <Box sx={{ mt: 4 }}>
          <NavigationPathways />
        </Box>
      </Box>

      {/* Notifications */}
      <Box sx={{ 
        position: 'fixed',
        top: 20,
        left: 40,
        zIndex: 1000
      }}>
      {notifications.map(notification => (
        <FloatingNotifications key={notification.id} notification={notification} />
      ))}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <NotificationCube />
        <GradientText variant="h4" gradient="primary">Submit A Proposal</GradientText>
      </Box>
    </Box>
  );
};

export default Landing; 